import React, { useState } from 'react';
import { AvForm, AvField, AvInput, AvGroup } from 'availity-reactstrap-validation';
import { Button, Row, Col, Label } from 'reactstrap';
import { Link } from 'react-router-dom';
import Barcode from 'react-barcode';
import Moment from 'react-moment';
import { KTIcon, enumSystemPolicyType } from '../../../../../_metronic/helpers';
import { SystemPolicyChecker } from '../../../../../_metronic/helpers/components/SystemPolicyChecker';
import { Audit } from '../../../../../_metronic/helpers/components/Audit';
import { Content } from '../../../../../_metronic/helpers/components/Content';
import InfoRow from './InfoRow';

const ProductVariantsSection = ({
    product,
    variants,
    intl,
    validation,
    newVariant,
    onVariantSubmit,
    setVariantFormRef,
    isEditing
}) => {
    
    const [showAddForm, setShowAddForm] = useState(false);

    // Read-only view
    if (!isEditing) {
        return (
            <div className="card card-flush">
                <div className="card-header min-h-auto py-4">
                    <div className="card-title">
                        <h3 className='fw-bold m-0'>{intl.formatMessage({ id: 'PRODUCT.VARIANTS.TITLE' })} <span className='text-muted fs-6 ms-2'>({variants && variants.length})</span></h3>
                    </div>
                </div>
                <div className="card-body pt-0">
                    <div className="table-responsive">
                        <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
                            <thead>
                                <tr className="fw-bolder text-muted">
                                    <th className="min-w-150px">{intl.formatMessage({ id: 'PRODUCT.FIELD.NAME' })}</th>
                                    <th className="min-w-100px">{intl.formatMessage({ id: 'PRODUCT.FIELD.CODE' })}</th>
                                    <th className="min-w-100px">{intl.formatMessage({ id: 'PRODUCT.FIELD.POSITION' })}</th>
                                    <th className="min-w-100px">{intl.formatMessage({ id: 'PRODUCT.FIELD.STOCKQUANTITY' })}</th>
                                    <th className="min-w-100px">{intl.formatMessage({ id: 'STOCK.FIELD.THRESHOLDS' })}</th>
                                    <th className="min-w-100px">{intl.formatMessage({ id: 'PRODUCT.FIELD.WEIGHT' })}</th>
                                    <th className="min-w-100px">{intl.formatMessage({ id: 'VERANDA_CALCULATOR.DIMENSIONS_SECTION' })}</th>
                                    <th className="min-w-100px">{intl.formatMessage({ id: 'PRODUCT.FIELD.ENABLED' })}</th>
                                    <th className="min-w-100px text-end">{intl.formatMessage({ id: 'GENERAL.ACTIONS' })}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {variants && variants.map((variant) => (
                                    <tr key={variant.id}>
                                        <td>
                                            <div className="text-dark fw-bolder text-hover-primary fs-6">{variant.name}</div>
                                        </td>
                                        <td>
                                            <span className="text-dark fw-bolder d-block fs-6">{variant.code}</span>
                                        </td>
                                        <td>
                                            <span className="text-dark fw-bolder d-block fs-6">{variant.position}</span>
                                        </td>
                                        <td>
                                            <span className={`badge badge-light-${variant.stockQuantity > variant.redThreshold ? 'success' : 'danger'}`}>
                                                {variant.stockQuantity}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="text-danger fw-bolder fs-6">{variant.redThreshold}</span>
                                            <span className="text-gray-400 fw-bold fs-6 mx-2">/</span>
                                            <span className="text-warning fw-bolder fs-6">{variant.yellowThreshold}</span>
                                        </td>
                                        <td>
                                            <span className="text-dark fw-bolder d-block fs-6">{variant.weight} {variant.weightUnit}</span>
                                        </td>
                                        <td>
                                            {(variant.width > 0 || variant.height > 0) && (
                                                <span className="text-dark fw-bolder d-block fs-6">
                                                    {variant.width > 0 && variant.height > 0 ? (
                                                        <>{variant.width} x {variant.height} {variant.measurementUnit}</>
                                                    ) : variant.width > 0 ? (
                                                        <>{variant.width} {variant.measurementUnit}</>
                                                    ) : (
                                                        <>{variant.height} {variant.measurementUnit}</>
                                                    )}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`badge badge-light-${variant.isActive ? 'success' : 'danger'}`}>
                                                {variant.isActive ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </td>
                                        <td className="text-end">
                                            <SystemPolicyChecker policyId={enumSystemPolicyType.StockControl}>
                                                <Link to={`/stock-management/stocks-print/${variant.id}`} className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1" title={intl.formatMessage({ id: 'GENERAL.PRINT' })}>
                                                    <KTIcon iconName='printer' className='fs-3' />
                                                </Link>
                                                <Link to={`/stock-management/stocks-operation/${variant.id}`} className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1" title={intl.formatMessage({ id: 'PRODUCT.FIELD.ADDDROPSTOCK' })}>
                                                    <KTIcon iconName='scan-barcode' className='fs-3' />
                                                </Link>
                                                <Link to={`/stock-management/stocks/${variant.id}`} className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm" title={intl.formatMessage({ id: 'STOCK.FIELD.STOCK' })}>
                                                    <KTIcon iconName='dropbox' className='fs-3' />
                                                </Link>
                                            </SystemPolicyChecker>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )
    }

    // Edit Mode
    return (
        <div className="mb-5 mb-xl-10">
            {/* Add Variant Button */}
            <div className="d-flex justify-content-between align-items-center mb-6">
                 <h3 className="fw-bold m-0 text-gray-800">{intl.formatMessage({ id: 'PRODUCT.VARIANTS.TITLE' })}</h3>
                 {!showAddForm && (
                     <button className="btn btn-sm btn-primary" onClick={() => setShowAddForm(true)}>
                         <i className="ki-duotone ki-plus fs-2"></i> {intl.formatMessage({ id: 'PRODUCT.FIELD.ADDVARIANT' })}
                     </button>
                 )}
            </div>

            {/* Add Variant Form */}
            {showAddForm && (
                <div className='card mb-6 shadow-sm border border-dashed border-primary'>
                    <div className='card-header min-h-auto py-4 bg-light-primary'>
                        <h3 className="card-title fw-bold fs-4 text-primary">{intl.formatMessage({ id: 'PRODUCT.FIELD.ADDVARIANT' })}</h3>
                        <div className="card-toolbar">
                            <button className="btn btn-sm btn-icon btn-light-danger" onClick={() => setShowAddForm(false)}>
                                <i className="ki-duotone ki-cross fs-2"></i>
                            </button>
                        </div>
                    </div>
                    <div className="card-body py-4">
                        <AvForm key="new-variant-form" onValidSubmit={onVariantSubmit} ref={setVariantFormRef}>
                            <div className='row g-4'>
                                <div className='col-lg-3'>
                                    <AvField name="name" label={intl.formatMessage({ id: 'PRODUCT.FIELD.NAME' })} type="text" value={newVariant.name} validate={validation} className="form-control form-control-solid" placeholder="Variant Name" />
                                </div>
                                <div className='col-lg-3'>
                                    <AvField name="code" label={intl.formatMessage({ id: 'PRODUCT.FIELD.CODE' })} type="text" value={newVariant.code} validate={validation} className="form-control form-control-solid" placeholder="Code" />
                                </div>
                                <div className='col-lg-3'>
                                    <AvField name="position" label={intl.formatMessage({ id: 'PRODUCT.FIELD.POSITION' })} type="text" value={newVariant && newVariant.position} className="form-control form-control-solid" placeholder="Position" />
                                </div>
                                <div className='col-lg-3 d-flex align-items-center mt-6'>
                                    <div className="form-check form-check-custom form-check-solid">
                                        <AvInput type="checkbox" name="isActive" value={newVariant.isActive} className="form-check-input" id="newActive" />
                                        <label className="form-check-label fw-bold ps-2" htmlFor="newActive">{intl.formatMessage({ id: 'PRODUCT.FIELD.ENABLED' })}</label>
                                    </div>
                                </div>

                                <div className='col-lg-2'>
                                    <AvField name="weight" label={intl.formatMessage({ id: 'PRODUCT.FIELD.WEIGHT' })} type="number" value={newVariant.weigth} validate={validation} className="form-control form-control-solid" />
                                </div>
                                <div className='col-lg-2'>
                                    <AvField type="select" name="weightUnit" label={intl.formatMessage({ id: 'PRODUCT.FIELD.UNIT' })} validate={validation} value={newVariant.weightUnit} className="form-select form-select-solid">
                                        <option value=''>{intl.formatMessage({ id: 'GENERAL.SELECT' })}</option>
                                        <option value='g'>g</option>
                                        <option value='kg'>kg</option>
                                        <option value='t'>t</option>
                                    </AvField>
                                </div>
                                
                                <div className='col-lg-2'>
                                    <AvField name="width" label={intl.formatMessage({ id: 'PRODUCT.FIELD.WIDTH' })} type="number" value={newVariant.width} validate={validation} className="form-control form-control-solid" />
                                </div>
                                <div className='col-lg-2'>
                                    <AvField name="height" label={intl.formatMessage({ id: 'PRODUCT.FIELD.HEIGHT' })} type="number" value={newVariant.height} validate={validation} className="form-control form-control-solid" />
                                </div>
                                <div className='col-lg-2'>
                                    <AvField type="select" name="measurementUnit" label={intl.formatMessage({ id: 'PRODUCT.FIELD.UNIT' })} validate={validation} value={newVariant.weightUnit} className="form-select form-select-solid">
                                        <option value=''>{intl.formatMessage({ id: 'GENERAL.SELECT' })}</option>
                                        <option value='mm'>mm</option>
                                        <option value='cm'>cm</option>
                                        <option value='m'>m</option>
                                    </AvField>
                                </div>

                                <div className='col-lg-3'>
                                    <AvField name="redThreshold" label={intl.formatMessage({ id: 'PRODUCT.FIELD.REDTHRESHOLD' })} type="number" value={newVariant.redThreshold} validate={validation} className="form-control form-control-solid" />
                                </div>
                                <div className='col-lg-3'>
                                    <AvField name="yellowThreshold" label={intl.formatMessage({ id: 'PRODUCT.FIELD.YELLOWTHRESHOLD' })} type="number" value={newVariant.yellowThreshold} validate={validation} className="form-control form-control-solid" />
                                </div>
                            </div>

                            <div className="d-flex justify-content-end mt-4">
                                <AvField type="hidden" name="id" value={0} />
                                <AvField type="hidden" name="productId" value={product && product.id} />
                                <Button color="primary" type="submit" className="px-6">{intl.formatMessage({ id: 'GENERAL.SAVE' })}</Button>
                            </div>
                        </AvForm>
                    </div>
                </div>
            )}

            {/* List of Variants (Editable Grid) */}
            <div className="row g-5">
                {variants && variants.map((item, index) => (
                    <div key={`variant_${index}`} className="col-xl-4 col-md-6">
                        <div className='card card-flush h-100 shadow-sm border-hover-primary'>
                            <div className='card-header min-h-auto py-6 px-6 d-flex align-items-center justify-content-between border-0 bg-light'>
                                <div className="card-title m-0">
                                    <div className="d-flex flex-column">
                                        <h4 className="fw-bold m-0 fs-5 text-gray-800 text-truncate mw-150px" title={item.name}>{item.name}</h4>
                                        <span className="text-muted fs-7 fw-bold">{item.code}</span>
                                    </div>
                                </div>
                                <div className="card-toolbar m-0">
                                    <div className="d-flex align-items-center">
                                        <SystemPolicyChecker policyId={enumSystemPolicyType.StockControl}>
                                            <div className="d-flex">
                                                <Link to={`/stock-management/stocks-print/${item.id}`} className="btn btn-icon btn-light-info btn-sm me-1" title={intl.formatMessage({ id: 'GENERAL.PRINT' })}> 
                                                    <KTIcon iconName='printer' className='fs-3' />
                                                </Link>
                                                <Link to={`/stock-management/stocks-operation/${item.id}`} className="btn btn-icon btn-light-info btn-sm me-1" title={intl.formatMessage({ id: 'PRODUCT.FIELD.ADDDROPSTOCK' })}>
                                                    <KTIcon iconName='scan-barcode' className='fs-3' />
                                                </Link>
                                                <Link to={`/stock-management/stocks/${item.id}`} className="btn btn-icon btn-light-info btn-sm me-1" title={intl.formatMessage({ id: 'STOCK.FIELD.STOCK' })}> 
                                                    <KTIcon iconName='dropbox' className='fs-3' />
                                                </Link>
                                            </div>
                                        </SystemPolicyChecker>
                                        <div>
                                            <Audit table="Variant" identifier={item.id} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="card-body py-6 px-6">
                                <AvForm onValidSubmit={onVariantSubmit}>
                                    <div className='row g-3'>
                                        <div className='col-12'>
                                            <AvGroup className="mb-2">
                                                <Label className="fs-6 fw-bold mb-1">{intl.formatMessage({ id: 'PRODUCT.FIELD.NAME' })}</Label>
                                                <AvInput name="name" value={item.name} validate={validation} className="form-control form-control-solid" />
                                            </AvGroup>
                                        </div>
                                        <div className='col-6'>
                                             <AvField name="code" label={intl.formatMessage({ id: 'PRODUCT.FIELD.CODE' })} type="text" value={item.code} validate={validation} className="form-control form-control-solid" labelClass="fs-6 fw-bold mb-1" />
                                        </div>
                                        <div className='col-6'>
                                            <AvField name="position" label={intl.formatMessage({ id: 'PRODUCT.FIELD.POSITION' })} type="text" value={item && item.position} className="form-control form-control-solid" labelClass="fs-6 fw-bold mb-1" />
                                        </div>

                                        <div className='col-6'>
                                            <AvField name="weight" label={intl.formatMessage({ id: 'PRODUCT.FIELD.WEIGHT' })} type="number" value={item.weight || 0} validate={validation} className="form-control form-control-solid" labelClass="fs-6 fw-bold mb-1" />
                                        </div>
                                        <div className='col-6'>
                                            <AvField type="select" name="weightUnit" label={intl.formatMessage({ id: 'PRODUCT.FIELD.UNIT' })} validate={validation} value={item.weightUnit} className="form-select form-select-solid" labelClass="fs-6 fw-bold mb-1">
                                                <option value=''>{intl.formatMessage({ id: 'GENERAL.SELECT' })}</option>
                                                <option value='g'>g</option>
                                                <option value='kg'>kg</option>
                                                <option value='t'>t</option>
                                            </AvField>
                                        </div>

                                        <div className='col-4'>
                                            <AvField name="width" label={intl.formatMessage({ id: 'PRODUCT.FIELD.WIDTH' })} type="number" value={item.width !== undefined && item.width !== null ? item.width : 0} validate={validation} className="form-control form-control-solid" labelClass="fs-6 fw-bold mb-1" />
                                        </div>
                                        <div className='col-4'>
                                            <AvField name="height" label={intl.formatMessage({ id: 'PRODUCT.FIELD.HEIGHT' })} type="number" value={item.height !== undefined && item.height !== null ? item.height : 0} validate={validation} className="form-control form-control-solid" labelClass="fs-6 fw-bold mb-1" />
                                        </div>
                                        <div className='col-4'>
                                            <AvField type="select" name="measurementUnit" label={intl.formatMessage({ id: 'PRODUCT.FIELD.UNIT' })} validate={validation} value={item.measurementUnit} className="form-select form-select-solid" labelClass="fs-6 fw-bold mb-1">
                                                <option value='mm'>mm</option>
                                                <option value='cm'>cm</option>
                                                <option value='m'>m</option>
                                            </AvField>
                                        </div>
                                        
                                        <div className='col-6'>
                                            <AvField name="redThreshold" label={intl.formatMessage({ id: 'PRODUCT.FIELD.REDTHRESHOLD' })} type="number" value={item.redThreshold || 0} validate={validation} className="form-control form-control-solid" labelClass="fs-6 fw-bold mb-1 text-danger" />
                                        </div>
                                        <div className='col-6'>
                                            <AvField name="yellowThreshold" label={intl.formatMessage({ id: 'PRODUCT.FIELD.YELLOWTHRESHOLD' })} type="number" value={item.yellowThreshold || 0} validate={validation} className="form-control form-control-solid" labelClass="fs-6 fw-bold mb-1 text-warning" />
                                        </div>
                                    </div>
                                    
                                    <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                                        <div className="form-check form-check-custom form-check-solid">
                                            <AvInput type="checkbox" name="isActive" value={item.isActive} className="form-check-input" id={`active_${item.id}`} />
                                            <label className="form-check-label text-gray-700 fs-6 fw-bold" htmlFor={`active_${item.id}`}>Enabled</label>
                                        </div>
                                        <div>
                                             <AvField type="hidden" name="id" value={item.id} />
                                             <AvField type="hidden" name="productId" value={item.productId} />
                                             <Button color="light-primary" type="submit" className="btn-sm">Save</Button>
                                        </div>
                                    </div>
                                </AvForm>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProductVariantsSection;
